import styled from '@emotion/styled';
import {
  UilTrash,
  UilPen,
  UilPlus,
  UilImport,
} from '@iconscout/react-unicons';
import {
  Button,
  ConfirmDialog,
  DialogContent,
  Divider,
  IconButton,
  Waiting,
  ButtonGroup,
  Box,
  Notification,
} from '@xbotvn/mui';
import { get, values } from 'lodash';
import moment from 'moment';
import React, {
  useMemo,
  useState,
  useEffect,
} from 'react';
import {
  useDispatch, useSelector,
} from 'react-redux';

import { paginationApi } from '../../../api';
import Table from '../../../components/Table';
import {
  cloneDeep,
  getOr,
  map,
  forEach,
} from '../../../libs/lodash';
import {
  STORAGE,
} from '../../../redux/actions/constants';

import EditorModal from './EditorModal';
import exportHandler from './export';

export default function Storage() {
  const dispatch = useDispatch();

  const {
    storages,
    borrows,
    bookshelves,
    warehouses,
    documents,
    handling,
    activeUnit,
    producers,
    categorys,
  } = useSelector(({
    storage,
    document,
    bookshelf,
    warehouse,
    user,
    borrow,
    producer,
    category,
  }) => ({
    borrows: borrow.data,
    storages: storage.data,
    bookshelves: bookshelf.data,
    warehouses: warehouse.data,
    documents: document.data,
    handling: storage.handling,
    activeUnit: user.activeUnit,
    producers: producer.data,
    categorys: category.data,
  }));

  const [editor, setEditor] = useState();
  const [counts, setCounts] = useState({});
  const [removeConfirm, setRemoveConfirm] = useState(false);

  useEffect(() => {
    paginationApi.getCount('documentItem', {
      condition: map(
        ({ _id }) => ({
          storageId: _id,
          unitId: activeUnit,
          borrowed: true,
        }),
        storages,
      ),
    }).then(({
      data,
    }) => {
      const {
        count,
      } = data;
      const records = {};
      forEach(
        ({ count: borrowed, storageId }) => {
          records[storageId] = borrowed;
        },
        count,
      );
      setCounts(records);
    });
  }, []);

  const data = useMemo(() => map(
    (elm) => ({
      ...elm,
      docName: getOr('', [elm.documentId, 'name'], documents),
      bookshelfName: getOr('', [elm.bookshelfId, 'name'], bookshelves),
      warehouseName: getOr('', [elm.warehouseId, 'name'], warehouses),
    }),
    storages,
  ), [storages, documents, bookshelves, warehouses]);

  const columns = useMemo(() => [
    {
      Header: 'STT',
      style: {
        width: 80,
      },
      Cell: ({
        row,
      }) => row.index + 1,
    },
    {
      Header: 'Tài liệu',
      accessor: 'docName',
      style: {
        width: 350,
      },
      Cell: (rowProps) => (
        <DivWarning amount={getOr(0, 'amount', get(rowProps, 'row.original'))}>
          {get(rowProps, 'value')}
        </DivWarning>
      ),
    },
    {
      Header: 'Kệ sách',
      accessor: 'bookshelfName',
      style: {
        width: 350,
      },
      Cell: (rowProps) => (
        <DivWarning amount={getOr(0, 'amount', get(rowProps, 'row.original'))}>
          {get(rowProps, 'value')}
        </DivWarning>
      ),
    },
    {
      Header: 'Kho',
      accessor: 'warehouseName',
      style: {
        width: 350,
      },
      Cell: (rowProps) => (
        <DivWarning amount={getOr(0, 'amount', get(rowProps, 'row.original'))}>
          {get(rowProps, 'value')}
        </DivWarning>
      ),
    },
    {
      Header: 'Số lượng',
      accessor: 'amount',
      style: {
        width: 200,
      },
      Cell: (rowProps) => (
        <DivWarning amount={parseFloat(get(rowProps, 'value'))}>
          {get(rowProps, 'value')}
        </DivWarning>
      ),
    },
    {
      Header: 'Đang mượn',
      accessor: 'borrowedAmount',
      style: {
        width: 200,
      },
      Cell: (rowProps) => (
        <DivWarning amount={getOr(0, 'amount', get(rowProps, 'row.original'))}>
          {counts?.[get(rowProps, 'row.original._id')] ?? 0}
        </DivWarning>
      ),
    },
    {
      Header: 'Còn lại',
      accessor: 'remainingAmount',
      style: {
        width: 200,
      },
      Cell: (rowProps) => (
        <DivWarning amount={getOr(0, 'amount', get(rowProps, 'row.original'))}>
          {(get(rowProps, 'row.original.amount') ?? 0) - (counts?.[get(rowProps, 'row.original._id')] ?? 0)}
        </DivWarning>
      ),
    },
    {
      Header: 'Ngày nhập',
      accessor: 'dateAdded',
      style: {
        width: 250,
      },
      Cell: (rowProps) => {
        if (!get(rowProps, 'value')) return '';
        return (
          <DivWarning amount={getOr(0, 'amount', get(rowProps, 'row.original'))}>
            {moment(get(rowProps, 'value')).format('DD/MM/YYYY')}
          </DivWarning>
        );
      },
    },
    {
      Header: () => (
        <Button
          startIcon={<UilPlus />}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => setEditor({
            amount: 0,
            documentId: '',
            bookshelfId: '',
            warehouseId: '',
            dateAdded: new Date(),
            unitId: activeUnit,
          })}
        >
          Thêm
        </Button>
      ),
      id: 'add',
      style: {
        width: 150,
      },
      Cell: (rowProps) => {
        const rowValue = getOr({}, 'row.original', rowProps);
        return (
          <ButtonGroup>
            <IconButton
              onClick={() => setEditor(cloneDeep(rowValue))}
              color="primary"
              size="small"
              sx={{ marginRight: '5px' }}
            >
              <UilPen />
            </IconButton>
            <Divider orientation="vertical" flexItem />
            <IconButton
              size="small"
              color="error"
              sx={{ marginLeft: '5px' }}
              onClick={() => {
                setRemoveConfirm(rowValue._id);
              }}
            >
              <UilTrash />
            </IconButton>
          </ButtonGroup>
        );
      },
    },
  ], [storages, documents, bookshelves, warehouses, borrows, counts]);

  return (
    <div>
      {editor ? <EditorModal data={editor} onClose={() => setEditor()} /> : null}
      {handling && <Waiting fullscreen />}
      {removeConfirm ? (
        <ConfirmDialog
          title="Các mã sách đã được lưu trữ cũng sẽ bị xóa. Bạn có chắc chắn muốn xóa?"
          color="error"
          confirmLabel="Xoá"
          cancelLabel="Huỷ"
          maxWidth="sm"
          fullWidth
          open
          onClose={() => setRemoveConfirm()}
          onConfirm={() => {
            dispatch({
              type: STORAGE.handlers.remove,
              ids: [removeConfirm],
            });
            setRemoveConfirm();
          }}
        >
          <DialogContent />
        </ConfirmDialog>
      ) : null}
      <Box marginLeft={1} padding={1}>
        <Button
          startIcon={<UilImport />}
          color="primary"
          size="small"
          sx={{ marginRight: 1 }}
          onClick={() => exportHandler({
            year: new Date().getFullYear(),
            documents,
            storages: data,
            activeUnit,
            producers,
            categorys,
            onSuccess: () => Notification.success('Xuất thành công'),
          })}
        >
          Xuất sổ ĐKCB
        </Button>
      </Box>
      <Table columns={columns} data={values(data)} alwaysShowPagination />
    </div>
  );
}

const DivWarning = styled.div`
  background: ${({ amount }) => {
    switch (amount) {
      case 2: return '#ff0000';
      case 5: return '#ffa500';
      case 10: return '#ffff00';
      default: return 'transparent';
    }
  }};
`;
